/**
 * @fileoverview Email verification component
 * 
 * This file implements the email verification page for the leader profile application.
 * It allows users to verify their email address by providing the email associated with
 * their invitation and validating it against a token provided in the URL.
 * 
 * Key features:
 * - Email validation with error handling
 * - Token-based verification process
 * - Confirmation modal for user feedback
 * - Unauthorized access handling
 * - Client-specific branding
 */

import React, { useContext, useEffect, useState } from 'react';
import { Button, ButtonVariant, IconName, ButtonIconAlign, TextInputWidth, LabelSize, TextInput } from '@eightfold.ai/octuple';
import Header from '../commons/Header';
import { secondaryButtonStyle } from '../commons/commonStyles';
import Footer from '../commons/Footer';
import Api from '../Api';
import { globalContext } from '../store';
import { useLocation, useNavigate } from 'react-router';
import { getOnboardingImg, getHeaderLogo } from '../commons/commonFunctions';
import { Link, useSearchParams } from 'react-router-dom';
import { validateFields, validationOptions } from '../ValidationSchema';
import ConfirmationModal from '../commons/ConfirmationModal';

/**
 * Email verification component
 * 
 * This component allows users to verify their email address by entering it and
 * confirming it with a token provided in the URL. If verification is successful,
 * users are redirected to the welcome page. If the token is missing or invalid,
 * an unauthorized access message is displayed.
 * 
 * @returns {JSX.Element} The rendered email verification page or unauthorized message
 */
const VerifyEmail = () => {
  // Get token from URL query parameters
  let [params] = useSearchParams();
  let navigate = useNavigate();
  let token = params?.get('token');
  const location = useLocation();
  const receivedData = location.state;

  // Access global state for client information
  const { globalState } = useContext(globalContext);

  // State variables for component
  const [email, setEmail] = useState<any>('');
  const [unauthorized, setUnauthorized] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [continueButtonLoader, setContinueButtonLoader] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [showModal, setShowModal] = useState(false);

  // Get client information for branding
  const title = globalState?.clientData?.name;
  // Use test domain for localhost development
  let subDomain: any = window.location.host === 'localhost:3000' ? 'validation.test-leaders.heidricknavigator.com' : window.location.host;

  /**
   * Check if token exists on component mount
   * If token is missing, show unauthorized message
   */
  useEffect(() => {
    if (!token) {
      setUnauthorized(true);
    }
  }, [token]);

  /**
   * Validates the email address and shows confirmation modal if valid
   * 
   * This function:
   * 1. Shows a loader during validation
   * 2. Validates the email format using the validation schema
   * 3. Shows an error message if validation fails
   * 4. Shows a confirmation modal if email is valid
   */
  const confirmEmail = () => {
    setShowLoader(true);
    const emailMsg = validateFields.validate({ email: email ? email : '' }, validationOptions)?.error?.details[0]?.message;

    if (emailMsg) {
      setErrorMsg(emailMsg);
      setShowLoader(false);
    } else {
      setShowModal(true);
      setErrorMsg('');
    }
  };

  /**
   * Handles closing the confirmation modal
   * Resets all loader states
   */
  const onClose = () => {
    setShowModal(false);
    setShowLoader(false);
    setContinueButtonLoader(false);
  };

  /**
   * Handles the verification process when user confirms email
   * 
   * This function:
   * 1. Shows a loader during the API call
   * 2. Sends email and subdomain to the verification API with token
   * 3. Redirects to welcome page on success
   * 4. Shows error messages from API response on failure
   */
  const onContinue = () => {
    setContinueButtonLoader(true);
    const payload = {
      email: email,
      subDomain: subDomain,
      profileType: receivedData?.userDetails?.profileType,
    };

    Api.getEmailVerify(payload, token)
      .then((response: any) => {
        setContinueButtonLoader(false);
        if (response?.success) {
          // Verification successful, redirect to welcome page
          setShowModal(false);
          navigate('/welcome');
        } else {
          // Handle error responses from API
          if (response?.data?.detail) {
            setShowModal(false);
            setShowLoader(false);
          }
          if (response?.data?.message) {
            setShowModal(false);
            setShowLoader(false);
          }
        }
      })
      .catch((err: any) => {
        console.log('err', err);
      });
  };

  return (
    <>
      {/* Show unauthorized message if token is missing */}
      {unauthorized ? (
        <h1 className='inviteVerifyError'>Unauthorized Access</h1>
      ) : (
        <div className='mainWrapper'>
          {/* Left panel with branding image */}
          <div
            className='imgWrapper'
            style={{
              background: `url(${getOnboardingImg(globalState)}) no-repeat`,
            }}
          >
            &nbsp;
          </div>
          {/* Right panel with content */}
          <div className='rightWrapper'>
            {/* Header with client logo */}
            <Header title={title} headerLogo={getHeaderLogo(globalState)} />
            <div className='contentWrapper'>
              <h1 className='titleStyle'>Verify Your Contact Information</h1>
              <div className='middleContent' style={{ width: '100%' }}>
                <p className='welcomeContent'> Enter the email address associated with your invitation.</p>

                {/* Email input field with validation */}
                <div className='fieldWrap' style={{ width: '100%' }}>
                  <TextInput
                    htmlType='text'
                    id='email'
                    clearable
                    labelProps={{
                      text: 'Email Address',
                      size: LabelSize.Medium,
                    }}
                    maxlength={100}
                    minlength={0}
                    name='email'
                    value={email}
                    onChange={(e: any) => {
                      setEmail(e.target.value);
                    }}
                    inputWidth={TextInputWidth.fill}
                    classNames='inputTextField'
                    status={errorMsg ? 'error' : 'validating'}
                  />
                  {/* Error message display */}
                  {errorMsg && <span className='errorMessage'>{errorMsg}</span>}
                </div>
                {/* Confirm button with loading state */}
                <div>
                  <Button
                    text='Confirm'
                    variant={ButtonVariant.Secondary}
                    classNames='secondaryButton'
                    style={secondaryButtonStyle}
                    onClick={() => {
                      confirmEmail();
                    }}
                    iconProps={showLoader ? { path: IconName.mdiLoading, spin: true } : undefined}
                    alignIcon={ButtonIconAlign.Right}
                    disabled={showLoader}
                  />
                </div>
                {/* Help text with support link */}
                <p className='welcomeContent' style={{ alignSelf: 'auto' }}>
                  If you did not receive an email&nbsp;
                  <Link style={{ color: '#1A212E' }} className='paragraphStyle' rel='noreferrer' to={'https://heidricksupport.zendesk.com/hc/en-us'} target='_blank'>
                    Contact Us
                  </Link>
                </p>
              </div>
            </div>
            {/* Page footer */}
            <Footer />
          </div>
        </div>
      )}
      {/* Confirmation modal */}
      {showModal && (
        <ConfirmationModal
          header='Check Your Email for Next Steps'
          showModal={showModal}
          bodyText='Please check your email for a temporary password to set up your secure account.'
          showCancelButton={false}
          onClose={onClose}
          onContinue={onContinue}
          continueButtonLoader={continueButtonLoader}
        />
      )}
    </>
  );
};

/**
 * Export the VerifyEmail component as the default export
 * This component is used in AppRouter.tsx for the email verification route
 */
export default VerifyEmail;
